import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, Modal, Table } from "antd";
import "../../App.css";
import "../MortgageCalculators/RateQuote.css";
import { checkAccess } from "../../Auth";
import { UR_Owner, UR_PriceEngineUser } from "../../State";
import AutoProgress from "../../DocumentChecklist/AutoProgress";
import { YellowWarning } from "../../Common/Warnings";
import ClosingCostFeeModal from "./components/closingCostDetail/closingCostFeeModal";
import PpeResults from "./components/result/PpeResults";
import AlternativeSuggestions from "./components/result/AlternativeSuggestions";
import CompareModal from "./components/result/CompareModal";

// The pricing engine page's result area (plan ppe-simplify-epps.md, work
// package 6). The filter bar above it (components/result/index.js) owns the
// requests, one per loan term; this component shows what came back: a loading
// table, the request error, or the results (components/result/PpeResults.js),
// plus the fee sheet, Compare and "no lenders added yet" dialogs.
//
// Until 11-02 this file was a fork of the legacy Rates page (3,000 lines of
// form, GMCC Celebrity and CRA logic the pricing engine page never used).

const tableHeight = () => `calc(100vh - ${checkAccess([UR_PriceEngineUser]) ? 52 : 322}px - 80px)`;

const COLUMNS = ["Lender", "Program", "Rate/APR", "Price", "Comp.", "Final price", "Closing cost", "PMT"].map((title) => ({ title, key: title, className: "outerRateColumn" }));

function EmptyTable({ children }) {
  return (
    <div style={{ width: "100%", padding: "8px" }}>
      <Table
        className="outertable rates-page-table full-table rates-page-table-new"
        dataSource={[]}
        pagination={false}
        scroll={{ y: tableHeight() }}
        locale={{ emptyText: children }}
        columns={COLUMNS}
      />
    </div>
  );
}

export default function RateQuoteNewResultTable(props) {
  const {
    loading,
    error,
    noLendersAdded,
    searchKey,
    products,
    ineligible,
    searchParams,
  } = props;
  const currentLoan = useSelector((state) => state.application && state.application.selection && state.application.selection.selectedloan);
  const [feeSheetRecord, setFeeSheetRecord] = useState(null);
  const [compareRows, setCompareRows] = useState(null);
  const [showAddLenderModal, setShowAddLenderModal] = useState(false);

  useEffect(() => {
    if (noLendersAdded) setShowAddLenderModal(true);
  }, [noLendersAdded, searchKey]);

  let body;
  if (loading) {
    body = (
      <EmptyTable>
        <AutoProgress type="circle" showInfo={false} percent="auto" strokeWidth={12} trailColor="#BDCBFB" strokeColor="#325CEB" size={60} />
      </EmptyTable>
    );
  } else if (error && products.length === 0 && ineligible.length === 0) {
    body = (
      <EmptyTable>
        <div className="no-matched-product-found">
          <div className="text-bold">{error.title || "Pricing request failed"}</div>
          <div className="text-normal">{error.message}</div>
        </div>
      </EmptyTable>
    );
  } else if (products.length === 0 && ineligible.length === 0) {
    body = (
      <div className="errorMessageWraper">
        <YellowWarning text={props.zeroText || "No qualified products."} />
        <AlternativeSuggestions alternatives={props.alternatives} onTry={props.onTryAlternative} />
      </div>
    );
  } else {
    body = (
      <div className={props.isMobile ? "resultContainer-mobile" : "ppe-results-card"} style={{ height: "100%" }}>
        <PpeResults
          {...props}
          onViewFeeSheet={(record) => setFeeSheetRecord({ ...record, compensationType: searchParams.compensationType })}
          onCompare={(rows) => setCompareRows(rows)}
        />
      </div>
    );
  }

  return (
    <div id="contain" style={{ backgroundColor: "#ffffff", color: "black", borderRadius: 12 }} className="w-100 h-100 text-center">
      {body}
      <ClosingCostFeeModal
        show={Boolean(feeSheetRecord)}
        onHide={() => setFeeSheetRecord(null)}
        filterParams={searchParams}
        selectedLoan={currentLoan}
        detailedCard={feeSheetRecord || {}}
        closingCostsDetail={props.appliedClosingCostsDetail}
        detailType="all"
        statsigSource="rates"
        isExporting={false}
      />
      <CompareModal
        rows={compareRows}
        onRemove={(key) => setCompareRows((rows) => {
          const next = (rows || []).filter(({ product }) => product.id !== key);
          return next.length ? next : null;
        })}
        onClose={() => setCompareRows(null)}
      />
      <Modal className="lender-tip-modal" open={showAddLenderModal} onCancel={() => setShowAddLenderModal(false)} footer={null} centered>
        <div className="lender-tip-modal-content">
          <div style={{ color: "#222", fontSize: 20, fontWeight: 600, lineHeight: "normal", marginBottom: 30 }}>You haven't added any company-level lender settings yet.</div>
          <div className="d-flex" style={{ fontSize: 16, color: "#222", lineHeight: "20px" }}>
            {checkAccess([UR_Owner])
              ? "You can see general rates right now, but for rates tailored to your lenders, update your lender settings in the Lenders section on the Management page."
              : "While you can always check general rates, for a more tailored view based on your lenders, your account admin can update settings in the Management page's Lenders section."}
          </div>
        </div>
        <div className="lender-tip-modal-footer">
          {checkAccess([UR_Owner]) && (
            <Button className="lender-tip-modal-updateBtn" onClick={() => { window.location.href = "/app/management"; }}>
              Update my lenders
            </Button>
          )}
          <Button className="lender-tip-modal-ratesBtn" onClick={() => setShowAddLenderModal(false)}>
            View general rates now
          </Button>
        </div>
      </Modal>
    </div>
  );
}
